/**
 * Client-side traceability aggregation for a Goal.
 *
 * Given a goal and the workspace's tasks + estimate summaries (loaded once via
 * the existing task/estimate stores), this computes — ENTIRELY IN THE BROWSER —
 * the engineering-work rollup for the goal: how many linked stories exist, how
 * many are done / remaining / blocked, the aggregated engineering effort
 * (Total / Completed / Remaining) derived from any linked estimates, the set of
 * contributing engineers, and a lightweight delivery forecast.
 *
 * No backend accessors are added: linkage is expressed as `goal.linkedStoryIds`
 * (Task document ids), and effort is read from whatever numeric output an
 * estimate already produced (reusing the estimation engine's outputs).
 *
 * Pure TS — no React.
 */

import type { Task } from '../tasks/types';
import type { EstimateSummary } from '../estimation/types';
import type { EstimateOutputs } from '../estimation/types';
import type { Goal } from './types';

/** Aggregated engineering-work rollup for a goal. */
export interface GoalTraceability {
  /** Number of linked stories (tasks) that were resolvable. */
  totalStories: number;
  /** Linked stories in the DONE column. */
  completedStories: number;
  /** Linked stories not yet done. */
  remainingStories: number;
  /** Linked stories currently BLOCKED. */
  blockedStories: number;
  /** Story completion percentage (0..100); 0 when there are no linked stories. */
  storyCompletionPct: number;

  /** Whether ANY effort figure was derivable from linked estimates. */
  hasEffort: boolean;
  /** Total engineering effort across linked, estimated stories. */
  totalEffort: number;
  /** Effort attributed to completed (DONE) linked stories. */
  completedEffort: number;
  /** Effort still outstanding (total − completed). */
  remainingEffort: number;
  /** Unit label for the effort figures (best-effort inference). */
  effortUnit: string;

  /** Unique, sorted contributing engineer aliases (linked task assignees). */
  contributingEngineers: string[];
  /** The resolved linked tasks (for drill-down rendering). */
  linkedTasks: Task[];
  /** Linked story ids that could not be resolved to a task. */
  unresolvedStoryIds: string[];
  /** Linked stories with no attached estimate (unestimated work). */
  unestimatedStories: number;

  /** A short, human-readable delivery-forecast line. */
  deliveryForecast: string;
}

/**
 * Keys we treat as an "effort" signal on an estimate's outputs, in priority
 * order. The first numeric match wins. Matching is case-insensitive substring.
 */
const EFFORT_KEY_HINTS = [
  'personweeks',
  'person-weeks',
  'engineerweeks',
  'devweeks',
  'totaleffort',
  'effort',
  'weeks',
  'persondays',
  'person-days',
  'days',
  'storypoints',
  'points',
  'hours',
];

/** Rough unit label inferred from the winning effort key. */
function unitForKey(key: string): string {
  const k = key.toLowerCase();
  if (k.includes('week')) return 'person-weeks';
  if (k.includes('day')) return 'person-days';
  if (k.includes('point')) return 'points';
  if (k.includes('hour')) return 'hours';
  return 'effort';
}

/**
 * Extracts a single numeric effort figure (and its unit) from an estimate's
 * outputs by scanning for an effort-like key. Returns undefined when none of
 * the outputs look like effort.
 */
export function extractEffort(
  outputs: EstimateOutputs,
): { value: number; unit: string } | undefined {
  const entries = Object.entries(outputs);
  for (const hint of EFFORT_KEY_HINTS) {
    for (const [key, value] of entries) {
      if (
        key.toLowerCase().includes(hint) &&
        typeof value === 'number' &&
        Number.isFinite(value)
      ) {
        return { value, unit: unitForKey(key) };
      }
    }
  }
  // Fallback: the first finite numeric output, if any.
  for (const [key, value] of entries) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return { value, unit: unitForKey(key) };
    }
  }
  return undefined;
}

const DONE_STATUS = 'DONE';
const BLOCKED_STATUS = 'BLOCKED';

/**
 * Computes the traceability rollup for a goal from the workspace's tasks and
 * estimate summaries. `allTasks` / `allEstimates` are the full non-deleted
 * collections; this function selects the linked subset itself.
 */
export function computeTraceability(
  goal: Goal,
  allTasks: Task[],
  allEstimates: EstimateSummary[],
): GoalTraceability {
  const linkedIds = goal.linkedStoryIds ?? [];
  const taskById = new Map<string, Task>();
  for (const t of allTasks) taskById.set(t.id, t);
  const estimateById = new Map<string, EstimateSummary>();
  for (const e of allEstimates) estimateById.set(e.id, e);

  const linkedTasks: Task[] = [];
  const unresolvedStoryIds: string[] = [];
  for (const id of linkedIds) {
    const t = taskById.get(id);
    if (t) linkedTasks.push(t);
    else unresolvedStoryIds.push(id);
  }

  const totalStories = linkedTasks.length;
  const completedStories = linkedTasks.filter(
    (t) => t.status === DONE_STATUS,
  ).length;
  const blockedStories = linkedTasks.filter(
    (t) => t.status === BLOCKED_STATUS,
  ).length;
  const remainingStories = totalStories - completedStories;
  const storyCompletionPct =
    totalStories === 0
      ? 0
      : Math.round((completedStories / totalStories) * 100);

  let totalEffort = 0;
  let completedEffort = 0;
  let hasEffort = false;
  let unestimatedStories = 0;
  const units = new Map<string, number>();

  for (const t of linkedTasks) {
    const est = t.estimateId ? estimateById.get(t.estimateId) : undefined;
    const effort = est ? extractEffort(est.outputs) : undefined;
    if (!effort) {
      unestimatedStories += 1;
      continue;
    }
    hasEffort = true;
    totalEffort += effort.value;
    if (t.status === DONE_STATUS) completedEffort += effort.value;
    units.set(effort.unit, (units.get(effort.unit) ?? 0) + 1);
  }

  const remainingEffort = Math.max(0, totalEffort - completedEffort);
  const effortUnit = pickDominantUnit(units);

  const contributingEngineers = uniqueSorted(
    linkedTasks.map((t) => (t.assignee ?? '').trim()).filter(Boolean),
  );

  const deliveryForecast = buildForecast({
    totalStories,
    completedStories,
    blockedStories,
    storyCompletionPct,
    hasEffort,
    remainingEffort,
    effortUnit,
    unestimatedStories,
  });

  return {
    totalStories,
    completedStories,
    remainingStories,
    blockedStories,
    storyCompletionPct,
    hasEffort,
    totalEffort: round1(totalEffort),
    completedEffort: round1(completedEffort),
    remainingEffort: round1(remainingEffort),
    effortUnit,
    contributingEngineers,
    linkedTasks,
    unresolvedStoryIds,
    unestimatedStories,
    deliveryForecast,
  };
}

function pickDominantUnit(units: Map<string, number>): string {
  let best = 'effort';
  let bestCount = -1;
  for (const [unit, count] of units) {
    if (count > bestCount) {
      best = unit;
      bestCount = count;
    }
  }
  return best;
}

function buildForecast(args: {
  totalStories: number;
  completedStories: number;
  blockedStories: number;
  storyCompletionPct: number;
  hasEffort: boolean;
  remainingEffort: number;
  effortUnit: string;
  unestimatedStories: number;
}): string {
  if (args.totalStories === 0) {
    return 'No engineering stories are linked yet — link tasks to forecast delivery.';
  }
  if (args.completedStories === args.totalStories) {
    return 'All linked engineering work is complete.';
  }
  const parts: string[] = [];
  parts.push(
    `${args.completedStories}/${args.totalStories} linked stories done (${args.storyCompletionPct}%).`,
  );
  if (args.hasEffort && args.remainingEffort > 0) {
    parts.push(
      `~${round1(args.remainingEffort)} ${args.effortUnit} of estimated work remaining.`,
    );
  }
  if (args.blockedStories > 0) {
    parts.push(
      `${args.blockedStories} ${args.blockedStories === 1 ? 'story is' : 'stories are'} blocked.`,
    );
  }
  if (args.unestimatedStories > 0) {
    parts.push(
      `${args.unestimatedStories} linked ${args.unestimatedStories === 1 ? 'story has' : 'stories have'} no estimate.`,
    );
  }
  return parts.join(' ');
}

function uniqueSorted(values: string[]): string[] {
  const set = new Set<string>();
  for (const v of values) if (v) set.add(v);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
